if (!customElements.get('ncm-size-guide-modal')) {
  customElements.define(
    'ncm-size-guide-modal',
    class NcmSizeGuideModal extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[role="dialog"]');
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
        document.addEventListener('keydown', this.onKeyDown.bind(this), { signal });
        this.addEventListener(
          'click',
          (event) => {
            if (event.target === this || event.target.closest('[data-size-guide-close]')) this.close();
          },
          { signal }
        );

        this.decorateLinks();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (!this.hidden) this.toggleScrollLock(false);
      }

      get productInfo() {
        return document.getElementById(`MainProduct-${this.dataset.sectionId}`);
      }

      isSizeGuideLink(link) {
        if (!link.matches('a[href]') || this.contains(link)) return false;
        const url = new URL(link.href, window.location.origin);
        return (
          url.origin === window.location.origin &&
          url.pathname.replace(/\/$/, '').endsWith(`/pages/${this.dataset.pageHandle}`)
        );
      }

      // Links to the size guide page in editable rich text (e.g. the Size Guide / Fit row) open this modal.
      decorateLinks() {
        this.productInfo?.querySelectorAll('.accordion__content a[href]').forEach((link) => {
          if (!this.isSizeGuideLink(link)) return;
          link.setAttribute('aria-haspopup', 'dialog');
          // A link that is alone in its paragraph is styled as a button; inline links stay text links.
          const standalone = link.parentElement?.textContent.trim() === link.textContent.trim();
          link.classList.toggle('ncm-size-guide-link', standalone);
        });
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-size-guide-open], a[href]');
        if (!opener || !this.productInfo?.contains(opener) || this.contains(opener)) return;

        if (opener.matches('[data-size-guide-open]')) {
          const controls = opener.getAttribute('aria-controls');
          if (controls && controls !== this.id) return;
        } else if (!this.isSizeGuideLink(opener) || event.metaKey || event.ctrlKey || event.shiftKey) {
          return;
        }

        event.preventDefault();
        this.open(opener);
      }

      onKeyDown(event) {
        if (this.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
        ).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      toggleScrollLock(locked) {
        document.documentElement.classList.toggle('ncm-size-guide-modal-open', locked);
        document.body.classList.toggle('ncm-size-guide-modal-open', locked);
        if (locked) window.NcmScrollLock?.lock();
        else window.NcmScrollLock?.unlock();
      }

      open(opener) {
        this.opener = opener;
        this.hidden = false;
        this.toggleScrollLock(true);
        this.dialog.scrollTop = 0;
        this.querySelector('[data-size-guide-close]')?.focus();
      }

      close() {
        if (this.hidden) return;
        this.hidden = true;
        this.toggleScrollLock(false);

        // The variant picker is re-rendered on option change, so the original trigger may be gone.
        const returnFocus = this.opener?.isConnected
          ? this.opener
          : this.productInfo?.querySelector(`[data-size-guide-open][aria-controls="${this.id}"]`);
        returnFocus?.focus({ preventScroll: true });
      }
    }
  );
}
